/* eslint-disable operator-linebreak */
import flatten from 'lodash/flatten';
import mapKeys from 'lodash/mapKeys';
import trim from 'lodash/trim';

import { MenuEntriesColors } from '../types/config.types';
import { generateColorsVariant } from './colorsUtils';
import { fontFormatFromName } from './fontUtils';
import { replaceValues } from './stringUtils';

interface VariablesOptions {
  opacities?: number[];
  brightnesses?: Record<string, number>;
}
interface CustomFont {
  name: string;
  files?: Record<string, { uri: string }>;
}

export interface Fonts {
  bodyFont?: { kind?: string; category?: string; family?: string };
  customFont?: {
    body: CustomFont & {
      fallbacks?: 'custom' | 'systemfont';
    };
  };
  designFont?: { family: string };
}

export interface CSSVariables {
  [x: string]: Record<string, string | number | CSSVariables | undefined>;
}

const computeVariable = (
  variable: CSSVariables,
  prefix?: string,
): Record<string, string | number> | undefined => {
  let variables = {} as Record<string, string | number>;
  if (!variable) return undefined;

  Object.entries(variable).forEach(([key, value]) => {
    const finalKey = `${prefix ? `${prefix}-` : ''}${key}`;
    if (typeof value === 'string' || typeof value === 'number') {
      variables[finalKey] = value;
      return;
    }
    const nestedVariables = computeVariable(value as CSSVariables, finalKey);
    variables = { ...variables, ...nestedVariables };
  });

  return prefix ? variables : mapKeys(variables, (value, key) => `--ac-${key}`);
};

export const generateVariables = (
  designVariables: CSSVariables,
  options: VariablesOptions,
): string => {
  const { color, ...restVariables } = designVariables;

  // Generate color variants depending on brightness & opacity
  const generatedColors = color
    ? generateColorsVariant(color as Record<string, string>, {
        brightnesses: options.brightnesses,
        opacities: options.opacities,
      })
    : {};

  // Compute json to define css variable
  const computedStyles = computeVariable({ ...restVariables, color: generatedColors });

  if (!computedStyles) return '';
  // Inject css variables in root & iFrame's root
  const formattedVariables = Object.entries(computedStyles)
    .map(([key, value]) => {
      return `${key}: ${value.toString()};`;
    })
    .join('\n');

  return `:root{${formattedVariables}}`;
};

export const generateClass = (
  classToCreate: Record<string, string>,
  designVariables: CSSVariables,
): string => {
  const { size } = designVariables;

  const generatedClass = Object.entries(classToCreate).map(([key, value]) => {
    return Object.entries(size).map(([sizeKey, sizeValue]) => {
      return `.ac-${key}-${sizeKey}{
            ${replaceValues(value, { size: sizeValue })}
          }`;
    });
  });

  return flatten(generatedClass).join('\n');
};

const generateFontFaces = (font: CustomFont): string => {
  if (!font || !font?.files) return '';
  const { name = 'default-class', files } = font;

  const generatedFontFaces = Object.keys(files).map((type) => {
    const file = files[type];
    if (!name || !file || !file.uri) return '';
    const format = fontFormatFromName(file.uri);
    if (!format) return '';

    return `
          /* ${type} */
         @font-face {
            font-family: "${trim(name, '"')}";
            src: url("${file.uri}") format("${format}");
            ${type === 'italic' || type === 'boldItalic' ? '  font-style: italic;' : ''}
            ${type === 'bold' || type === 'boldItalic' ? '  font-weight: bold;' : ''}
          }`;
  });

  return generatedFontFaces.join('\n');
};

export const generateFonts = ({
  classNames,
  // bodyFont,
  customFont,
  designFont,
}: Fonts & { classNames: string }): string => {
  const font = customFont?.body;
  if (!font?.name) {
    return `
        ${classNames} {
          font-family: ${designFont?.family || 'sans-serif'};
        }`;
  }

  return `${generateFontFaces(font)} 
      
      ${classNames} {
        font-family: "${trim(font.name, '"')}", ${font.fallbacks || 'sans-serif'} !important;
      }
      `;
};

// Hover and the selected item share the same brightness filter on the item's
// own colors (no alpha, no hardcoded color).
const MENU_HIGHLIGHT_BRIGHTNESS = 1.5;

// Generates the main menu color overrides only when colors are configured, so
// events without this config keep their default (Semantic/primary) styling
// untouched. Selectors are variant-qualified to beat Semantic UI's own
// `a.item:hover` and `.active.item` rules.
export const generateMenuStyle = (colors: MenuEntriesColors | undefined): string => {
  const item = colors?.item;
  if (!item || (!item.color && !item.background && !item.borderColor)) return '';

  const text = item.color;
  const bg = item.background;
  const activeBorder = item.borderColor || bg;

  const textDecl = text ? `color: ${text};` : '';
  const bgDecl = bg ? `background: ${bg};` : '';
  const activeBorderDecl = activeBorder ? `border-color: ${activeBorder};` : '';

  return `
    .ui.menu.menu--main .item { ${textDecl} ${bgDecl} }

    .ui.menu.menu--main:not(.secondary) .item:hover,
    .ui.menu.menu--main.secondary:not(.pointing) .item:hover,
    .ui.menu.menu--main.secondary.pointing .item:hover {
      ${textDecl} ${bgDecl} filter: brightness(${MENU_HIGHLIGHT_BRIGHTNESS});
    }

    .ui.menu.menu--main:not(.secondary) .active.item {
      ${textDecl} ${bgDecl} filter: brightness(${MENU_HIGHLIGHT_BRIGHTNESS});
    }

    .ui.menu.menu--main.secondary:not(.pointing) .active.item,
    .ui.menu.menu--main.secondary:not(.pointing) .active.item:hover {
      ${textDecl} ${bgDecl} filter: brightness(${MENU_HIGHLIGHT_BRIGHTNESS});
    }

    .ui.menu.menu--main.secondary.pointing .active.item,
    .ui.menu.menu--main.secondary.pointing .active.item:hover {
      ${textDecl} ${bgDecl} ${activeBorderDecl} filter: brightness(${MENU_HIGHLIGHT_BRIGHTNESS});
    }
  `;
};
